import { saveAs } from "file-saver";
import { ICheckedForecast } from "store/features";
import * as XLSX from "xlsx";

import { ForecastFolder, UserArpsItem } from "models/UserArpsModel";

export enum WellDataTemplateColumn {
  WellName = "Well Name",
  ReserveCategory = "Reserve Category",
  HzLength = "Hz Length",
  StageSpacing = "Stage Spacing",
  ProppantIntensity = "Proppant Intensity"
}

interface TypeWellRow {
  [WellDataTemplateColumn.WellName]: string;
  [WellDataTemplateColumn.ReserveCategory]: string;
  [WellDataTemplateColumn.HzLength]: string;
  [WellDataTemplateColumn.StageSpacing]: string;
  [WellDataTemplateColumn.ProppantIntensity]: string;
}

/**
 * Recursively collects all type wells from a folder and its nested subfolders
 */
function collectTypeWellsFromFolder(
  folder: ForecastFolder | ICheckedForecast,
  typeWells: TypeWellRow[] = []
): TypeWellRow[] {
  // Process children of the folder
  if (folder.children && folder.children.length > 0) {
    folder.children.forEach((child) => {
      if (child.isFolder) {
        collectTypeWellsFromFolder(child, typeWells);
      } else {
        const typeWell = child as UserArpsItem;
        typeWells.push({
          [WellDataTemplateColumn.WellName]: typeWell.title || "",
          [WellDataTemplateColumn.ReserveCategory]: typeWell.reserveCategory || "",
          [WellDataTemplateColumn.HzLength]: "",
          [WellDataTemplateColumn.StageSpacing]: "",
          [WellDataTemplateColumn.ProppantIntensity]: ""
        });
      }
    });
  }

  return typeWells;
}

/**
 * Generates and downloads an Excel template pre-populated with type well names
 * and reserve categories from the selected folder
 */
export function downloadWellDataTemplate(folder: ForecastFolder): void {
  const typeWellRows = collectTypeWellsFromFolder(folder);

  // If no type wells found, create an empty template with example values
  if (typeWellRows.length === 0) {
    typeWellRows.push({
      [WellDataTemplateColumn.WellName]: "xxxxx",
      [WellDataTemplateColumn.ReserveCategory]: "P+PDP",
      [WellDataTemplateColumn.HzLength]: "123",
      [WellDataTemplateColumn.StageSpacing]: "123",
      [WellDataTemplateColumn.ProppantIntensity]: "123"
    });
  }

  // Create worksheet from data
  const worksheet = XLSX.utils.json_to_sheet(typeWellRows);

  // Set column widths for better readability
  // Reserve Category column is hidden but still present for backend processing
  worksheet["!cols"] = [
    { wch: 30 }, // Well Name
    { wch: 20, hidden: true }, // Reserve Category - hidden from user view
    { wch: 15 }, // Hz Length
    { wch: 15 }, // Stage Spacing
    { wch: 18 } // Proppant Intensity
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Well Data");

  const sanitizedFolderName = folder.folderName.replace(/[^a-zA-Z0-9]/g, "_");
  const filename = `${sanitizedFolderName} Well Data Upload Template.xlsx`;

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });

  saveAs(blob, filename);
}

/**
 * Hook that provides the download function for well data templates
 */
export function useWellDataTemplateDownload() {
  return {
    downloadTemplate: downloadWellDataTemplate
  };
}
